import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { defineComponent } from 'vue';
import { useStyleConfig } from '../abstract';
import useThemeStore from './store';
import { toHsl, toHex } from '../utils/colors';
function filterMiddleColors(colors) {
  var text = undefined;
  var bg = undefined;
  Object.entries(colors).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      k = _ref2[0],
      v = _ref2[1];
    if (['bg-base', 'bg-layout', 'bg-container', 'bg-container', 'bg-popover', 'bg-spotlight', 'bg-mask', 'bg-hover', 'bg-disabled', 'bg-side', 'bg-header', 'bg-menu', 'bg-submenu'].indexOf(k) !== -1) {
      var _bg;
      bg = (_bg = bg) !== null && _bg !== void 0 ? _bg : {};
      bg[k] = v;
    } else {
      var _text;
      text = (_text = text) !== null && _text !== void 0 ? _text : {};
      text[k] = v;
    }
  });
  return {
    text: text,
    bg: bg
  };
}
/**
 * 计算对比度
 * @param l1
 * @param l2
 */
var contrast = function contrast(l1, l2) {
  return Math.max(l1, l2) / Math.min(l1, l2);
};
/**
 * 根据对比度计算亮度
 * @param contrast
 * @param tl
 */
var lightOfContrast = function lightOfContrast(ct, tl, ol) {
  var tc = contrast(tl, ol);
  if (tc) {
    ct = Math.pow(tc, 1 / 3) * ct;
  }
  return tl <= 0.1 ? tl * ct : tl / ct;
};
export default defineComponent({
  name: 'ThemeProvider',
  inheritAttrs: false,
  props: {
    themeClassName: String,
    isRoot: Boolean,
    applyStyle: {
      default: true,
      type: Boolean
    },
    autoAdapt: {
      default: true,
      type: Boolean
    },
    color: Object,
    font: Object,
    padding: Object,
    margin: Object,
    shadow: Object,
    size: Object,
    radius: Object
  },
  setup: function setup() {
    var _useStyleConfig = useStyleConfig('theme-config'),
      prefixCls = _useStyleConfig.prefixCls;
    var themeStore = useThemeStore();
    var setFont = themeStore.setFont,
      setSize = themeStore.setSize,
      setRadius = themeStore.setRadius,
      setShadow = themeStore.setShadow,
      setMargin = themeStore.setMargin,
      setPadding = themeStore.setPadding,
      setPrimaryColor = themeStore.setPrimaryColor,
      setFunctionalColor = themeStore.setFunctionalColor,
      setBgSeriesColors = themeStore.setBgSeriesColors,
      setTextSeriesColors = themeStore.setTextSeriesColors,
      rootTheme = themeStore.theme;
    return {
      rootTheme: rootTheme,
      prefixCls: prefixCls,
      setFont: setFont,
      setSize: setSize,
      setShadow: setShadow,
      setRadius: setRadius,
      setMargin: setMargin,
      setPadding: setPadding,
      setPrimaryColor: setPrimaryColor,
      setFunctionalColor: setFunctionalColor,
      setBgSeriesColors: setBgSeriesColors,
      setTextSeriesColors: setTextSeriesColors
    };
  },
  computed: {
    themeCfgCls: function themeCfgCls() {
      var _this$themeClassName;
      return this.isRoot ? '' : (_this$themeClassName = this.themeClassName) !== null && _this$themeClassName !== void 0 ? _this$themeClassName : this.prefixCls + this.$.uid;
    },
    registerCls: function registerCls() {
      var _this$themeClassName2;
      return this.isRoot ? ':root' : ".".concat((_this$themeClassName2 = this.themeClassName) !== null && _this$themeClassName2 !== void 0 ? _this$themeClassName2 : this.prefixCls + this.$.uid);
    }
  },
  watch: {
    'color.middle.bg-base': {
      handler: function handler(val) {
        var _this = this;
        if (this.isRoot || !val) {
          return false;
        }
        this.$nextTick(function () {
          _this.adapt(val, _this.rootTheme.color.middle['bg-container']);
        });
      },
      immediate: true
    },
    'color.middle.bg-container': {
      handler: function handler(val) {
        var _this2 = this;
        if (this.isRoot || !val) {
          return false;
        }
        this.$nextTick(function () {
          _this2.adapt(val, _this2.rootTheme.color.middle['bg-container']);
        });
      },
      immediate: true
    },
    'rootTheme.color.middle.bg-container': {
      handler: function handler(val) {
        var _this$color$middle$bg;
        this.adapt((_this$color$middle$bg = this.color.middle['bg-container']) !== null && _this$color$middle$bg !== void 0 ? _this$color$middle$bg : this.color.middle['bg-base'], val);
      }
    },
    color: {
      // eslint-disable-next-line complexity
      handler: function handler(val) {
        if (val) {
          var _bg$bgBase;
          var selector = this.registerCls;
          var _val$middle = val.middle,
            middle = _val$middle === void 0 ? {} : _val$middle,
            warning = val.warning,
            success = val.success,
            info = val.info,
            error = val.error,
            primary = val.primary;
          var _filterMiddleColors = filterMiddleColors(middle),
            text = _filterMiddleColors.text,
            bg = _filterMiddleColors.bg;
          var contentBg = (_bg$bgBase = bg === null || bg === void 0 ? void 0 : bg['bg-base']) !== null && _bg$bgBase !== void 0 ? _bg$bgBase : bg === null || bg === void 0 ? void 0 : bg['bg-container'];
          if (text) this.setTextSeriesColors(text, selector);
          if (warning) this.setFunctionalColor('warning', warning, selector, contentBg);
          if (success) this.setFunctionalColor('success', success, selector, contentBg);
          if (info) this.setFunctionalColor('info', info, selector, contentBg);
          if (error) this.setFunctionalColor('error', error, selector, contentBg);
          if (primary) this.setPrimaryColor(primary, selector, contentBg);
          if (bg) this.setBgSeriesColors(bg, true, selector);
        }
      },
      deep: true,
      immediate: true
    },
    size: {
      handler: function handler(val) {
        if (val) {
          this.setSize(val, this.registerCls);
        }
      },
      deep: true,
      immediate: true
    },
    font: {
      handler: function handler(val) {
        if (val) {
          this.setFont(val, this.registerCls);
        }
      },
      deep: true,
      immediate: true
    },
    padding: {
      handler: function handler(val) {
        if (val) {
          this.setPadding(val, this.registerCls);
        }
      },
      deep: true,
      immediate: true
    },
    margin: {
      handler: function handler(val) {
        if (val) {
          this.setMargin(val, this.registerCls);
        }
      },
      deep: true,
      immediate: true
    },
    radius: {
      handler: function handler(val) {
        if (val) {
          this.setRadius(val, this.registerCls);
        }
      },
      deep: true,
      immediate: true
    },
    shadow: {
      handler: function handler(val) {
        if (val) {
          this.setShadow(val, this.registerCls);
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // eslint-disable-next-line complexity
    adapt: function adapt(color, rootBg) {
      if (this.isRoot || !color || !this.autoAdapt) {
        return;
      }
      var _toHsl = toHsl(rootBg),
        rl = _toHsl.l,
        rh = _toHsl.h,
        rs = _toHsl.s;
      var _toHsl2 = toHsl(color),
        l = _toHsl2.l,
        h = _toHsl2.h,
        s = _toHsl2.s;
      // 明度相差小于 0.45 直接使用原色值
      if (Math.abs(rl - l) <= 0.45) {
        this.setBgSeriesColors({
          'bg-base': color
        }, true, this.registerCls);
        return;
      }
      var rol = rl <= 0.75 ? 0.99 : 0.1;
      var diff = l - rol;
      var _contrast = contrast(l, rol);
      var _dl = rl + diff < 0.085 || rl + diff > 1 ? rl - diff : rl + diff;
      var _rl = lightOfContrast(_contrast, rl, rol);
      // const _l = rh == h && rs == s ? _dl : Math.pow(_rl * _dl, 1 / 2);
      var _l = rh == h && rs == s ? _dl : (_dl + _rl) / 2;
      color = '#' + toHex("hsl(".concat(h * 360, ", ").concat(s, ", ").concat(_l, ")"));
      this.setBgSeriesColors({
        'bg-base': color
      }, true, this.registerCls);
    }
  },
  render: function render() {
    var _this$$slots$default,
      _this$$slots,
      _this3 = this;
    var slot = (_this$$slots$default = (_this$$slots = this.$slots).default) === null || _this$$slots$default === void 0 ? void 0 : _this$$slots$default.call(_this$$slots);
    slot.forEach(function (item) {
      var _item$props, _item$props$class, _this3$$attrs$class;
      item.props = (_item$props = item.props) !== null && _item$props !== void 0 ? _item$props : {};
      item.props.class = "".concat(_this3.themeCfgCls, " ").concat(_this3.applyStyle ? 'bg-container text-text text-base border-border' : '', " ").concat((_item$props$class = item.props.class) !== null && _item$props$class !== void 0 ? _item$props$class : '', " ").concat((_this3$$attrs$class = _this3.$attrs.class) !== null && _this3$$attrs$class !== void 0 ? _this3$$attrs$class : '');
    });
    return slot;
  }
});